// components/CheckinModal.js - Location-based check-in modal
import React, { useState } from 'react';
import { db } from '../firebase';
import { updateDoc, doc } from 'firebase/firestore';

function CheckinModal({ checkinOpportunity, isOpen, onClose, onCheckinSuccess }) {
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const handleCheckin = async () => {
    if (!checkinOpportunity?.event?.userRegistration) return;

    setChecking(true);
    setError('');

    try {
      const registration = checkinOpportunity.event.userRegistration;
      
      // Update registration with check-in info
      await updateDoc(doc(db, 'registrations', registration.id), {
        checkedIn: true,
        checkedInAt: new Date().toISOString(),
        checkedInLocation: {
          lat: checkinOpportunity.userLocation.lat,
          lng: checkinOpportunity.userLocation.lng,
          accuracy: checkinOpportunity.userLocation.accuracy
        },
        distanceToVenue: checkinOpportunity.distance
      });

      // Call success callback
      if (onCheckinSuccess) {
        onCheckinSuccess(checkinOpportunity.event);
      }

      // Show success message
      showNotification('Successfully checked in to the event!', 'success');
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Error checking in:', error);
      setError('Failed to check in. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg max-w-sm ${
      type === 'success' ? 'bg-green-500 text-white' : 
      type === 'error' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
    }`;
    notification.innerHTML = `
      <div class="flex items-center justify-center text-center">
        <span class="font-medium">${message}</span>
      </div>
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 4000);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (!isOpen || !checkinOpportunity) return null;

  const { event, distance, minutesUntilStart } = checkinOpportunity;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-2xl">
        {/* Header with location indicator */}
        <div className="bg-gradient-to-r from-green-500 to-blue-500 p-6 text-white">
          <div className="text-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">You're at the venue!</h3>
            <p className="text-green-100 text-sm">Ready to check in to your event?</p>
          </div>
        </div>

        {/* Event details */}
        <div className="p-6">
          <div className="text-center mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">{event.title}</h4>
            <div className="text-gray-600 space-y-1">
              <p className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Starts at {formatTime(event.startDate)}
              </p>
              <p className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                {event.venueName}
              </p>
            </div>
          </div>

          {/* Location confirmation */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center mb-3">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2 animate-pulse"></div>
              <span className="text-green-800 font-medium text-sm">Location Confirmed</span>
            </div>
            <div className="text-center space-y-1 text-sm text-green-700">
              <p>Distance to venue: <span className="font-medium">{distance}m</span></p>
              <p>
                {minutesUntilStart > 0 
                  ? `Event starts in ${Math.abs(minutesUntilStart)} minutes`
                  : minutesUntilStart > -15 
                    ? `Event started ${Math.abs(minutesUntilStart)} minutes ago`
                    : 'Event has started'
                }
              </p>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Check-in instructions */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h5 className="font-medium text-blue-800 text-sm mb-2">Check-in Benefits:</h5>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>• Confirm your attendance</li>
              <li>• Help organizers plan better</li>
              <li>• Build your attendance record</li>
              <li>• Meet other players arriving</li>
            </ul>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={checking}
              className="flex-1 px-4 py-3 rounded-lg font-medium border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Maybe Later
            </button>
            <button
              onClick={handleCheckin}
              disabled={checking}
              className="flex-1 px-4 py-3 rounded-lg font-medium bg-green-500 hover:bg-green-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checking ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Checking In...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Check In Now
                </div>
              )}
            </button>
          </div>

          {/* Privacy note */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Your location is only used for check-in verification and will not be shared with other users.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckinModal;